import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '@carbon/react';
import { ArrowDownRight, Box } from '@carbon/react/icons';
import { formatQuantity } from 'src/utils/inventory';

interface LowStockItem {
  itemId: string;
  itemName?: string;
  branchId: string;
  branchName?: string;
  quantity: number;
  threshold: number;
  unit?: string;
}

interface LowStockPanelProps {
  title?: string;
  lowStockItems: LowStockItem[];
}

/** How much of its threshold an item still has; 0 when out, below 1 when low. */
const coverage = (item: LowStockItem) => (item.threshold > 0 ? item.quantity / item.threshold : 0);

/**
 * The items at or below their low-stock threshold, as a list card: item and branch on the
 * left, a status pill, and what is left against the threshold on the right.
 *
 * Most urgent first — by how much of its threshold an item has left, not by raw quantity,
 * because "2 kg" of flour and "2 pcs" of lemons are not comparable. Out of stock is its own
 * status: an item at zero stops a dish, one merely low does not yet.
 */
const LowStockPanel = React.memo((props: LowStockPanelProps) => {
  const { t } = useTranslation('dashboard');
  const headingId = useId();
  const items = [...props.lowStockItems].sort((a, b) => coverage(a) - coverage(b));

  return (
    <section className="dashboard__stat_widget low-stock" aria-labelledby={headingId}>
      <div className="low-stock__header">
        <h4 id={headingId}>{props.title ?? t('lowStockItems')}</h4>
        {items.length > 0 && (
          <Tag type="red" size="sm">
            {items.length}
          </Tag>
        )}
      </div>
      {items.length === 0 ? (
        <p className="low-stock__empty">{t('translation:common.none')}</p>
      ) : (
        <>
          <div className="low-stock__columns" aria-hidden>
            <span>{t('item')}</span>
            <span>{t('status')}</span>
            <span>{t('inStock')}</span>
          </div>
          <ul className="low-stock__list" aria-labelledby={headingId}>
            {items.map((item) => {
              const out = Number(item.quantity) <= 0;
              const unit = item.unit || '';
              return (
                <li className="low-stock__row" key={`${item.itemId}-${item.branchId}`}>
                  <span className={`low-stock__avatar${out ? ' low-stock__avatar--out' : ''}`} aria-hidden>
                    <Box size={16} />
                  </span>
                  <span className="low-stock__name">
                    <span className="low-stock__item">{item.itemName || item.itemId}</span>
                    <span className="low-stock__branch">{item.branchName || item.branchId}</span>
                  </span>
                  <span className="low-stock__status">
                    <Tag type={out ? 'red' : 'warm-gray'} size="sm">
                      {out ? t('stockOut') : t('stockLow')}
                    </Tag>
                  </span>
                  <span className={`low-stock__level${out ? ' low-stock__level--out' : ''}`}>
                    <ArrowDownRight size={16} aria-hidden />
                    <span>
                      {formatQuantity(item.quantity, unit)}
                      <span className="low-stock__threshold"> / {formatQuantity(item.threshold, unit)}</span>
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
});

export default LowStockPanel;
